import { Card, Statistic, Row, Col, Tooltip, Typography } from "antd";
import {
  UserOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  ShopOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { HospitalityAnalytics } from "../interfaces/hospitalityAnalytics.interface";

const { Text } = Typography;

interface HospitalitySummaryCardsProps {
  summary: HospitalityAnalytics["summary"];
  isMobile: boolean;
}

export default function HospitalitySummaryCards({
  summary,
  isMobile,
}: HospitalitySummaryCardsProps) {
  const formatCurrency = (amount: number) => {
    if (typeof amount !== "number" || isNaN(amount)) {
      return "€0.00";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (num: number, decimals: number = 1) => {
    if (typeof num !== "number" || isNaN(num)) {
      return "0";
    }
    return num.toFixed(decimals);
  };

  return (
    <Row gutter={[16, 16]}>
      {/* Customer Metrics */}
      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Total Customers"
            value={summary.totalCustomers}
            prefix={<UserOutlined />}
            valueStyle={{ color: "#1890ff" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Repeat Customers"
            value={summary.repeatCustomers}
            prefix={<UserOutlined />}
            valueStyle={{ color: "#52c41a" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="One-Time Customers"
            value={summary.oneTimeCustomers}
            prefix={<UserOutlined />}
            valueStyle={{ color: "#faad14" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Repeat Rate"
            value={summary.repeatCustomerRate}
            suffix="%"
            valueStyle={{ color: "#722ed1" }}
            precision={1}
          />
        </Card>
      </Col>

      {/* Visit Frequency */}
      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Avg Visit Frequency"
            value={formatNumber(summary.averageVisitFrequency)}
            suffix="/month"
            prefix={<ThunderboltOutlined />}
            valueStyle={{ color: "#13c2c2" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Median Visit Frequency"
            value={formatNumber(summary.medianVisitFrequency)}
            suffix="/month"
            prefix={<ThunderboltOutlined />}
            valueStyle={{ color: "#13c2c2" }}
          />
        </Card>
      </Col>

      {/* Lifetime Value */}
      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Avg Lifetime Value"
            value={formatCurrency(summary.averageCustomerLifetimeValue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#3f8600" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Median Lifetime Value"
            value={formatCurrency(summary.medianCustomerLifetimeValue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#3f8600" }}
          />
        </Card>
      </Col>

      {/* Revenue */}
      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Total Revenue"
            value={formatCurrency(summary.totalRevenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#3f8600", fontSize: isMobile ? "18px" : "24px" }}
          />
        </Card>
      </Col>

      {/* Segment Counts */}
      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="VIP Customers"
            value={summary.vipCustomers}
            prefix={<TrophyOutlined />}
            valueStyle={{ color: "#fa8c16" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Regular Customers"
            value={summary.regularCustomers}
            prefix={<ShopOutlined />}
            valueStyle={{ color: "#1890ff" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Occasional Customers"
            value={summary.occasionalCustomers}
            prefix={<ShopOutlined />}
            valueStyle={{ color: "#722ed1" }}
          />
        </Card>
      </Col>

      {/* Revenue by Segment */}
      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="VIP Revenue"
            value={formatCurrency(summary.vipRevenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#fa8c16" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Regular Revenue"
            value={formatCurrency(summary.regularRevenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#1890ff" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="Occasional Revenue"
            value={formatCurrency(summary.occasionalRevenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#722ed1" }}
          />
        </Card>
      </Col>

      <Col xs={24} sm={12} md={8} lg={6}>
        <Card>
          <Statistic
            title="One-Time Revenue"
            value={formatCurrency(summary.oneTimeRevenue)}
            prefix={<DollarOutlined />}
            valueStyle={{ color: "#faad14" }}
          />
        </Card>
      </Col>

      {/* Top 10% */}
      <Col xs={24} sm={12} md={12} lg={8}>
        <Card>
          <Tooltip title="The top 10% of customers contribute this percentage of total revenue">
            <Statistic
              title="Top 10% Revenue Share"
              value={summary.top10PercentRevenuePercentage || 0}
              suffix="%"
              prefix={<TrophyOutlined />}
              valueStyle={{ color: "#eb2f96", fontSize: isMobile ? "24px" : "32px" }}
              precision={1}
            />
          </Tooltip>
          <Text type="secondary" style={{ fontSize: "12px", marginTop: 4, display: "block" }}>
            {formatCurrency(summary.top10PercentRevenue)} from {summary.top10PercentCustomerCount} customers
          </Text>
        </Card>
      </Col>
    </Row>
  );
}

